import * as React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Avatar,
  Box,
  BoxProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Typography,
  styled,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import axios from 'axios';
import { useField } from 'formik';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { uploadFileToS3 } from '../../../../../api/files/uploadFileToS3';
import { environment } from '../../../../../config';
import Icons from '../../../../../themes/icons';

const CROP_ZOOM_MIN = 1;
const CROP_ZOOM_MAX = 2;
const CROP_ZOOM_STEP = 0.1;

function getCroppedImg(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  mimeType: string
): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const x = pixelCrop.x * scaleX;
  const y = pixelCrop.y * scaleY;
  const w = pixelCrop.width * scaleX;
  const h = pixelCrop.height * scaleY;

  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(w);
  canvas.height = Math.floor(h);
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('No canvas context'));
  ctx.drawImage(image, x, y, w, h, 0, 0, w, h);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      },
      mimeType,
      0.92
    );
  });
}

type PhotoCardProps = BoxProps<'label'> & {
  error?: boolean;
};

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

type PhotoCardStyleProps = PhotoCardProps & {
  isDragging?: boolean;
};

const PhotoCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'error' && prop !== 'isDragging',
})<PhotoCardStyleProps>(({ theme, error, isDragging }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: `2px dashed ${isDragging ? '#1a1a1a' : '#939393'}`,
  borderColor: error ? '#DA5151' : isDragging ? '#1a1a1a' : undefined,
  borderRadius: '8px',
  backgroundColor: isDragging ? '#F5F5F5' : '#FEFEFE',
  width: '100%',
  cursor: 'pointer',
  minHeight: 340,
  transition: 'background-color 120ms ease, border-color 120ms ease',
}));

// Image validation constants
const IMAGE_REQUIREMENTS = {
  RECOMMENDED_WIDTH: 2000,
  RECOMMENDED_HEIGHT: 2000,
  MIN_SIZE: 100 * 1024, // 100KB in bytes
  MAX_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ACCEPTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png'],
};

type ValidationError = {
  type: 'size' | 'format';
  message: string;
};

type Props = {
  isShopifyProduct?: boolean;
};

const OfferPhoto: React.FC<Props> = ({ isShopifyProduct = false }) => {
  const [field, meta, helper] = useField<string>('offerImageKey');
  const [previewImg, setPreviewImg] = React.useState<string>();
  const [loading, setLoading] = React.useState<boolean>();
  const [isLocalSet, setIsLocalSet] = React.useState(false);
  const [validationError, setValidationError] = React.useState<ValidationError | null>(null);

  const [cropModalOpen, setCropModalOpen] = React.useState(false);
  const [imageSrcToCrop, setImageSrcToCrop] = React.useState<string | null>(null);
  const [fileToCrop, setFileToCrop] = React.useState<File | null>(null);
  const [crop, setCrop] = React.useState<Crop>();
  const [cropZoom, setCropZoom] = React.useState(1);
  const cropImgRef = React.useRef<HTMLImageElement>(null);

  const [isDragging, setIsDragging] = React.useState(false);
  const dragCounterRef = React.useRef(0);

  const validateImage = (file: File): Promise<ValidationError | null> => {
    return new Promise((resolve) => {
      // Check file format
      if (!IMAGE_REQUIREMENTS.ACCEPTED_FORMATS.includes(file.type)) {
        resolve({
          type: 'format',
          message: 'Please upload a valid image format (JPEG, PNG, or JPG)',
        });
        return;
      }

      // Check file size
      if (file.size < IMAGE_REQUIREMENTS.MIN_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too small. Minimum size is ${
            IMAGE_REQUIREMENTS.MIN_SIZE / 1024
          }KB`,
        });
        return;
      }

      if (file.size > IMAGE_REQUIREMENTS.MAX_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too large. Maximum size is ${
            IMAGE_REQUIREMENTS.MAX_SIZE / (1024 * 1024)
          }MB`,
        });
        return;
      }

      // Verify the file is a readable image
      const img = new Image();
      img.onload = () => {
        resolve(null); // No validation errors
      };

      img.onerror = () => {
        resolve({
          type: 'format',
          message: 'Unable to read image file. Please try a different image.',
        });
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const downloadAndUploadImageAsFile = async (imageUrl: string): Promise<void> => {
    try {
      setLoading(true);
      setValidationError(null);

      const response = await axios.get<Blob>(imageUrl, {
        responseType: 'blob',
      });

      const blob = response.data;

      const fileNameWithQuery = imageUrl.split('/').pop() || 'uploaded-file';
      const fileName = fileNameWithQuery.split('?')[0];
      const contentType = blob.type || 'application/octet-stream';

      const file = new File([blob], fileName, { type: contentType });

      if (file) {
        // Validate the downloaded image
        const validationResult = await validateImage(file);
        if (validationResult) {
          setValidationError(validationResult);
          setLoading(false);
          return;
        }

        const previewUrl = URL.createObjectURL(file);
        setPreviewImg(previewUrl);

        const s3Key = await uploadFileToS3(file);
        helper.setValue(s3Key);

        setLoading(false);
        setIsLocalSet(true);
      }
    } catch (error) {
      console.error('❌ Error in download/upload:', error);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (field.value && isShopifyProduct && !isLocalSet) {
      downloadAndUploadImageAsFile(field.value);
    }
  }, [isShopifyProduct, field.value, isLocalSet]);

  const handleCloseCropModal = React.useCallback(() => {
    if (imageSrcToCrop) URL.revokeObjectURL(imageSrcToCrop);
    setCropModalOpen(false);
    setImageSrcToCrop(null);
    setFileToCrop(null);
    setCrop(undefined);
    setCropZoom(1);
  }, [imageSrcToCrop]);

  const handleCropConfirm = React.useCallback(async () => {
    const img = cropImgRef.current;
    if (!img || !crop || !fileToCrop || crop.width === 0 || crop.height === 0) return;

    const file = fileToCrop;
    const cropState = crop;
    const mimeType = file.type || 'image/jpeg';
    setLoading(true);

    try {
      const pixelCrop = convertToPixelCrop(cropState, img.width, img.height);
      const blob = await getCroppedImg(img, pixelCrop, mimeType);
      handleCloseCropModal();

      const croppedFile = new File([blob], file.name, { type: mimeType });

      const validationResult = await validateImage(croppedFile);
      if (validationResult) {
        setValidationError(validationResult);
        setLoading(false);
        return;
      }

      const previewUrl = URL.createObjectURL(croppedFile);
      setPreviewImg(previewUrl);
      const s3Key = await uploadFileToS3(croppedFile);
      helper.setValue(s3Key);
    } catch (error) {
      console.error('Crop/upload failed:', error);
      setValidationError({
        type: 'size',
        message: 'Failed to crop or upload. Please try again.',
      });
    }
    setLoading(false);
  }, [crop, fileToCrop, helper, handleCloseCropModal]);

  const processFile = React.useCallback(async (file: File) => {
    setIsLocalSet(true);
    setValidationError(null);
    setLoading(true);

    const validationResult = await validateImage(file);

    if (validationResult) {
      setValidationError(validationResult);
      setLoading(false);
      return;
    }

    const dataUrl = URL.createObjectURL(file);
    setFileToCrop(file);
    setImageSrcToCrop(dataUrl);
    setCrop(undefined);
    setCropModalOpen(true);
    setLoading(false);
  }, []);

  const handleFileUpload = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      const file = event.target.files ? event.target.files[0] : undefined;
      if (event.target) (event.target as HTMLInputElement).value = '';

      if (file) await processFile(file);
    },
    [processFile],
  );

  const handleDragEnter = React.useCallback((event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    if (event.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = React.useCallback((event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = React.useCallback((event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = React.useCallback(
    async (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);

      const file = event.dataTransfer?.files?.[0];
      if (file) await processFile(file);
    },
    [processFile],
  );

  React.useEffect(() => {
    if (!field.value) {
      setPreviewImg(undefined);
      setValidationError(null);
    }

    if (field.value && field.value.startsWith('https://')) {
      setPreviewImg(field.value);
    } else if (field.value && !field.value.startsWith('https://')) {
      setPreviewImg(`${baseUrl}/${field.value}`);
    }
  }, [field.value]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          backgroundColor: '#FAFAFA',
          borderRadius: '10px',
          border: '1px solid #EFEFEF',
          p: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
          {/* <LightbulbOutlinedIcon sx={{ color: 'text.primary', fontSize: 22 }} /> */}
          <Icons.LightBulbIcon />
          <Typography fontWeight={500} fontSize='13px' color='text.primary'>
            Suggestion
          </Typography>
        </Box>
        <Typography fontSize='13px' color='text.secondary'>
          Upload a well-lit, professional, and captivating photo of your product or service.
        </Typography>
      </Box>
      <PhotoCard
        component='label'
        error={Boolean(validationError) || (meta.touched && Boolean(meta.error))}
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewImg ? (
          <Avatar
            src={previewImg}
            sx={{
              position: 'absolute',
              borderRadius: 'inherit',
              height: '100%',
              width: '100%',
              opacity: loading ? 0.5 : 1,
            }}
          />
        ) : (
          <Typography
            component='div'
            fontWeight='medium'
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '18px',
            }}
          >
            <Box component='img' src='/svg/upload.svg' sx={{ height: '2rem' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ textDecoration: 'underline', fontSize: '18px', fontWeight: 500 }}>
                Select a photo for your offer
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontWeight: 300, color: '#848484', fontSize: '15px' }}>
                  Recommended size:{' '}
                  <Typography component={'span'} sx={{ fontWeight: 400 }}>
                    2000x2000 px
                  </Typography>
                </Typography>
                <Typography sx={{ color: '#848484', fontSize: '15px' }}>
                  Min. 100KB - Max 5MB
                </Typography>
              </Box>
            </Box>
          </Typography>
        )}
        {loading && <CircularProgress />}
        <input
          hidden
          accept='image/jpeg,image/jpg,image/png,image/webp'
          type='file'
          onChange={handleFileUpload}
        />
      </PhotoCard>

      {/* Validation Error Display */}
      {validationError && (
        <Alert
          severity='error'
          sx={{ mt: 2, color: '#BF3D3D' }}
          onClose={() => setValidationError(null)}
        >
          {validationError.message}
        </Alert>
      )}

      {/* Formik Error Display */}
      {meta.touched && meta.error && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {meta.error}
        </Alert>
      )}

      {/* Crop photo modal */}
      <Dialog
        open={cropModalOpen}
        onClose={handleCloseCropModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            height: 'auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            pr: 6,
            position: 'relative',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          Crop photo
          <IconButton
            aria-label='close'
            onClick={handleCloseCropModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
          {imageSrcToCrop && (
            <Box>
              <Box sx={{ overflow: 'auto', mb: 2, maxHeight: '60vh' }}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  aspect={1}
                  circularCrop={false}
                  ruleOfThirds
                >
                  <img
                    ref={cropImgRef}
                    src={imageSrcToCrop}
                    alt='Crop'
                    style={{
                      display: 'block',
                      maxHeight: '60vh',
                      width: 'auto',
                      transform: `scale(${cropZoom})`,
                      transformOrigin: 'center center',
                    }}
                    onLoad={(e) => {
                      const { naturalWidth, naturalHeight } = e.currentTarget;
                      setCrop(
                        centerCrop(
                          makeAspectCrop(
                            { unit: '%', width: 90 },
                            1,
                            naturalWidth,
                            naturalHeight
                          ),
                          naturalWidth,
                          naturalHeight
                        )
                      );
                    }}
                  />
                </ReactCrop>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 0.5,
                }}
              >
                <IconButton
                  size='small'
                  onClick={() =>
                    setCropZoom((z) =>
                      Math.max(CROP_ZOOM_MIN, Math.min(CROP_ZOOM_MAX, z - CROP_ZOOM_STEP))
                    )
                  }
                  aria-label='Zoom out'
                >
                  <Icons.RemoveIcon />
                </IconButton>
                <Slider
                  value={cropZoom}
                  min={CROP_ZOOM_MIN}
                  max={CROP_ZOOM_MAX}
                  step={CROP_ZOOM_STEP}
                  onChange={(_, value) =>
                    setCropZoom(Array.isArray(value) ? value[0] : value)
                  }
                  valueLabelDisplay='auto'
                  valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size='small'
                  onClick={() =>
                    setCropZoom((z) =>
                      Math.max(CROP_ZOOM_MIN, Math.min(CROP_ZOOM_MAX, z + CROP_ZOOM_STEP))
                    )
                  }
                  aria-label='Zoom in'
                >
                  <Icons.AddIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCropConfirm} disabled={!crop} style={{flex: 1, height: '59px', fontWeight: 500, fontSize: '18px'}}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferPhoto;
